import { useQuery } from "@tanstack/react-query";
import { useCallback, useContext, useMemo, useState } from "react";
import { LessonLearnedPDFTemplate } from "../../components/PDF/Reporting/lesson-learned/LessonLearnedPDFTemplate";
import ReportingFormDrawerV2 from "../../components/Reporting/ReportingFormDrawerV2";
import { ReportStreamColumn } from "../../components/Reporting/stream/ReportStreamList";
import ReportStreamPage from "../../components/Reporting/stream/ReportStreamPage";
import {
	ReportStreamMeta,
	ReportStreamNumberCell,
	ReportStreamRowMenu,
	ReportStreamStatusChip,
	ReportStreamTitle,
} from "../../components/Reporting/stream/ReportStreamParts";
import { HomeContext } from "../../containers/Home";
import {
	ReportStreamConfig,
	useReportStream,
} from "../../hooks/useReportStream";
import {
	ReportItem,
	ReportStreamAssets,
} from "../../hooks/useReportStreamFilter";
import {
	useReportPhases,
	useReportStreamNames,
	useSharedRecords,
	useTenantName,
} from "../../hooks/useReportStreamLookups";
import { getDisciplineList } from "../../services/api-server/discipline";
import { getLessonLearnedList } from "../../services/api-server/lesson_learned";
import { getOriginList } from "../../services/api-server/origin";
import { useTenantStore } from "../../stores/tenantStore";
import { formatDateDdMmmYyyy } from "../../utils/date_formatter";
import {
	getLessonAuthorName,
	getLessonFilterLabel,
	getLessonFilterValue,
	getLessonMeta,
	getLessonSearchTexts,
	getLessonSubmittedOn,
	StreamLesson,
} from "../../utils/reporting/lessonStream";
import { createLessonLearnedLookupConfig } from "../../utils/reporting/lookupConfigs/lesson-learned";
import {
	collectAuthorEmails,
	getDateValue,
	getReportNumberValue,
	StreamPlaces,
} from "../../utils/reporting/reportStream";
import {
	DEFAULT_REPORT_STATUSES,
	ReportStatusKey,
} from "../../utils/reporting/reportStatusOptions";
import {
	collectReportExportEmails,
	fetchExportNames,
	getReportExportData,
	lookUpReportForExport,
} from "../../utils/reporting/reportStreamExport";
import { handleExportReport } from "../../utils/reports";

type LessonRow = ReportItem & StreamLesson;

// Lessons never reach Approved - Open, and one sent back to its author
// counts as In progress, as its page shows it.
const LESSON_STATUSES: ReportStatusKey[] = [
	"in_review",
	"in_progress",
	"final_review",
	"closed",
];

const NO_LESSONS: LessonRow[] = [];

const getLessonPath = (lesson: LessonRow) => `/lessons-learned/${lesson._id}`;

// Lessons aren't a Reporting Hub type, so the stream reads them itself: the
// tenant's submitted lessons, or every tenant's for the master tenant, open
// ones first, then the last changed.
async function fetchLessons(currentTenantKey: string, isMasterTenant: boolean) {
	const lessons = await getLessonLearnedList(
		{
			status: { $ne: "draft" },
			...(isMasterTenant ? {} : { operator: currentTenantKey }),
		},
		{ status: -1, modified_on: -1 }
	);
	return lessons ?? [];
}

const fetchDisciplines = (ids: string[]) =>
	getDisciplineList({ _id: { $in: ids } });
const fetchOrigins = (ids: string[]) => getOriginList({ _id: { $in: ids } });

// The lesson's PDF, as its page exports it: the page's lookups for this
// lesson alone, in its tenant only when that is one of the user's, and the
// names of its author and reviewer.
async function exportLesson(lesson: LessonRow, tenantKeys: string[]) {
	const operators =
		lesson.operator && tenantKeys.includes(lesson.operator)
			? [lesson.operator]
			: [];
	const [lookedUp, emailToName] = await Promise.all([
		lookUpReportForExport(lesson, createLessonLearnedLookupConfig(operators)),
		fetchExportNames(collectReportExportEmails(lesson)),
	]);
	await handleExportReport(
		getReportExportData(lookedUp, emailToName),
		"lesson_learned",
		LessonLearnedPDFTemplate
	);
}

function LessonLearnedStream() {
	const { currentTenantKey }: any = useContext(HomeContext);
	const isMasterTenant = useTenantStore((state) => state.isMasterTenant);
	const tenantList = useTenantStore((state) => state.tenantList);
	const getTenantName = useTenantName();
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	// A saved lesson is written into this entry (ReportingFormDrawerV2), so
	// the rows stay as loaded.
	const { data, isPending, isError, isFetching, refetch } = useQuery({
		queryKey: ["reporting-hub-data", "lesson_learned"],
		queryFn: () => fetchLessons(currentTenantKey, isMasterTenant),
		enabled: !!currentTenantKey,
		staleTime: 5 * 60 * 1000,
	});
	const lessons: LessonRow[] | undefined = Array.isArray(data)
		? data
		: undefined;
	const rows = lessons ?? NO_LESSONS;

	// Only the listed authors are looked up by name, and only the listed
	// lessons' phases, disciplines and origins.
	const authorEmails = useMemo(() => collectAuthorEmails(rows), [rows]);
	const authorNames = useReportStreamNames(
		["lesson-stream-authors"],
		authorEmails
	);
	const phaseMap = useReportPhases(rows, currentTenantKey);
	const disciplineMap = useSharedRecords(
		rows,
		"discipline",
		"report-stream-disciplines",
		fetchDisciplines
	);
	const originMap = useSharedRecords(
		rows,
		"origin",
		"report-stream-origins",
		fetchOrigins
	);

	// The Discipline and Origin filters name their options once the records
	// load. Their values are ids, or a legacy lesson's text.
	const config = useMemo<ReportStreamConfig<LessonRow>>(
		() => ({
			statuses: LESSON_STATUSES,
			defaultStatuses: LESSON_STATUSES.filter((key) =>
				DEFAULT_REPORT_STATUSES.includes(key)
			),
			hasInternalExternal: false,
			filters: [
				{
					key: "discipline",
					label: "Discipline",
					getValue: (lesson) => getLessonFilterValue(lesson.discipline),
					getOptionLabel: (value) =>
						getLessonFilterLabel(
							disciplineMap,
							value,
							"Unknown discipline",
							"discipline"
						),
				},
				{
					key: "origin",
					label: "Origin",
					getValue: (lesson) => getLessonFilterValue(lesson.origin),
					getOptionLabel: (value) =>
						getLessonFilterLabel(originMap, value, "Unknown origin", "origin"),
				},
			],
			sortValues: {
				number: (lesson) => getReportNumberValue(lesson.report_number),
				date: (lesson) => getDateValue(getLessonSubmittedOn(lesson)),
			},
		}),
		[disciplineMap, originMap]
	);

	const getSearchTexts = useCallback(
		(lesson: LessonRow, assets: ReportStreamAssets) =>
			getLessonSearchTexts(
				lesson,
				{ ...assets, phaseMap },
				disciplineMap,
				originMap,
				authorNames,
				getTenantName
			),
		[phaseMap, disciplineMap, originMap, authorNames, getTenantName]
	);

	const view = useReportStream(
		lessons,
		config,
		getSearchTexts,
		currentTenantKey,
		isMasterTenant
	);
	const places = useMemo<StreamPlaces>(
		() => ({ ...view.assets, phaseMap }),
		[view.assets, phaseMap]
	);

	const columns: ReportStreamColumn<LessonRow>[] = [
		{
			key: "number",
			header: "Report no.",
			width: "140px",
			narrow: "number",
			sortKey: "number",
			render: (lesson) => (
				<ReportStreamNumberCell
					to={getLessonPath(lesson)}
					reportNumber={lesson.report_number}
					operator={view.hasSeveralOperators ? lesson.operator : undefined}
				/>
			),
		},
		{
			key: "lesson",
			header: "Lesson",
			width: "minmax(180px, 1fr)",
			narrow: "main",
			render: (lesson) => {
				const { tag, parts } = getLessonMeta(
					lesson,
					places,
					disciplineMap,
					originMap
				);
				return (
					<>
						<ReportStreamTitle title={lesson.title} />
						<ReportStreamMeta tag={tag} parts={parts} />
					</>
				);
			},
		},
		{
			key: "date",
			header: "Submitted",
			width: "104px",
			narrow: "detail",
			sortKey: "date",
			render: (lesson) => formatDateDdMmmYyyy(getLessonSubmittedOn(lesson)),
		},
		{
			key: "author",
			header: "Author",
			width: "140px",
			narrow: "detail",
			render: (lesson) => (
				<span className="report-stream__ellipsis">
					{getLessonAuthorName(lesson, authorNames)}
				</span>
			),
		},
		{
			key: "status",
			header: "Status",
			width: "132px",
			narrow: "status",
			render: (lesson) => (
				<ReportStreamStatusChip
					status={lesson.status}
					openStatus={lesson.open_status}
				/>
			),
		},
	];

	return (
		<div className="generic-container overflow-auto">
			<ReportStreamPage
				title="Lessons learned"
				subtitle={
					isMasterTenant
						? "Lessons from every tenant."
						: "Your tenant's lessons."
				}
				noun="lesson"
				plural="lessons"
				newLabel="New lesson"
				onNew={() => setIsDrawerOpen(true)}
				emptyText={
					isMasterTenant
						? "Lessons from every tenant will show here."
						: "Lessons your tenant submits will show here."
				}
				searchPlaceholder="Search number, title, what happened, what was learned, asset"
				view={view}
				isLoading={isPending}
				loadFailed={isError && !lessons}
				isRetrying={isFetching}
				onRetry={() => void refetch()}
				columns={columns}
				getRowKey={(lesson) => lesson._id}
				renderRowMenu={(lesson) => (
					// No tenant in the link: the lesson routes have no tenant
					// switch, and the old copied link had none.
					<ReportStreamRowMenu
						reportNumber={lesson.report_number}
						path={getLessonPath(lesson)}
						onExport={() =>
							exportLesson(
								lesson,
								tenantList.map((tenant: any) => tenant?.role_key)
							)
						}
					/>
				)}
			/>
			<ReportingFormDrawerV2
				isOpen={isDrawerOpen}
				reportType="lesson_learned"
				onClose={() => setIsDrawerOpen(false)}
			/>
		</div>
	);
}

export default LessonLearnedStream;
